import CountryCode from "@mapmycustomers/shared/enum/CountryCode";
import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import LookupData, { RawLookupData } from "@mapmycustomers/shared/types/territory/LookupData";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import getBoundaryUrl from "@app/util/map/getBoundaryUrl";

class LookupDataService {
  private cache: Partial<Record<CountryCode, Promise<LookupData>>> = {};

  public getLookupDataPromise(countryCode: CountryCode): Promise<LookupData> {
    if (isDefined(this.cache[countryCode])) {
      return this.cache[countryCode]!;
    }

    this.cache[countryCode] = new Promise((resolve, reject) => {
      fetch(getBoundaryUrl("lookup", countryCode))
        .then((response) => response.json())
        .then((json: RawLookupData) => {
          resolve(this.rawLookupToData(json));
        })
        .catch(reject);
    });

    return this.cache[countryCode]!;
  }

  // Transform lookup data so it's easier to consume
  private rawLookupToData = (rawLookup: RawLookupData): LookupData => {
    const lookupData: LookupData = { [TerritoryLevel.ADM1]: {}, [TerritoryLevel.ADM2]: {} };
    (Object.keys(rawLookup) as AreaRegion["id"][]).forEach((level1Id) => {
      const level2Ids = Object.keys(rawLookup[level1Id]) as AreaRegion["id"][];
      let allLevel3Ids = [] as AreaRegion["id"][];
      lookupData[TerritoryLevel.ADM1][level1Id] = {
        [TerritoryLevel.ADM2]: level2Ids,
        [TerritoryLevel.ADM3]: [],
      };
      level2Ids.forEach((level2Id) => {
        const level3Ids = rawLookup[level1Id][level2Id];
        allLevel3Ids = [...allLevel3Ids, ...level3Ids];
        if (!lookupData[TerritoryLevel.ADM2][level2Id]) {
          lookupData[TerritoryLevel.ADM2][level2Id] = {
            [TerritoryLevel.ADM1]: [],
            [TerritoryLevel.ADM3]: [],
          };
        }
        lookupData[TerritoryLevel.ADM2][level2Id][TerritoryLevel.ADM1].push(level1Id);
        lookupData[TerritoryLevel.ADM2][level2Id][TerritoryLevel.ADM3] = Array.from(
          new Set([...level3Ids, ...lookupData[TerritoryLevel.ADM2][level2Id][TerritoryLevel.ADM3]])
        );
      });
      lookupData[TerritoryLevel.ADM1][level1Id][TerritoryLevel.ADM3] = Array.from(
        new Set(allLevel3Ids)
      );
    });

    return lookupData;
  };
}

export const lookupDataService = new LookupDataService();

export default LookupDataService;
